import { OmitDistributive } from '@core/common-legacy';
import type {
  OptionalId,
  ReportDashboardComponent,
  ReportDashboardComponentViewData,
  ReportDashboardGridComponent,
} from '@core/reports-types';
import { ReportDashboardComponentKind } from '@core/reports-types';
import { getNextGridPosition } from '@core/reports-util';
import { generateUuid } from '@core/utils-legacy';

type ViewDataBase = OmitDistributive<
  ReportDashboardComponentViewData,
  'settings' | 'kind' | 'startPoint' | 'endPoint'
>;

export const toComponentViewData = (
  c: ReportDashboardComponentViewData,
): ReportDashboardComponentViewData => {
  const base: ViewDataBase = {
    templateId: c.templateId,
    isAvailable: c.isAvailable,
    unavailableReason: c.unavailableReason,
    name: c.name,
    description: c.description,
    chartType: c.chartType,
    metric: c.metric,
    grid: c.grid,
    sortBy: c.sortBy,
    sortOrder: c.sortOrder,
    filters: c.filters,
    primaryGroup: c.primaryGroup,
    secondaryGroup: c.secondaryGroup,
    dateFilter: c.dateFilter,
    dateField: c.dateField,
    options: c.options,
    viewMode: c.viewMode,
    origin: c.origin,
    ...(c.generated !== undefined && { generated: c.generated }),
  };

  if (c.kind === ReportDashboardComponentKind.TimeBased) {
    return { ...base, kind: c.kind, startPoint: c.startPoint, endPoint: c.endPoint };
  }
  return { ...base, kind: c.kind };
};

export const beforeComponentCreate = (
  component: ReportDashboardComponentViewData | OptionalId<ReportDashboardComponent>,
  correlationId: string = generateUuid(),
  gridPositionOptions:
    | {
        resolveGridPosition: true;
        dashboardGrid: ReportDashboardGridComponent[];
      }
    | {
        resolveGridPosition: false;
      },
): OmitDistributive<ReportDashboardComponent, 'id'> => {
  let result: ReportDashboardComponentViewData;
  if ('id' in component) {
    const { id, ...rest } = component;
    result = <ReportDashboardComponentViewData>rest;
  } else {
    result = <ReportDashboardComponentViewData>component;
  }

  return {
    ...result,
    localId: correlationId,
    grid: gridPositionOptions.resolveGridPosition
      ? getNextGridPosition(gridPositionOptions.dashboardGrid, component.grid)
      : result.grid,
  };
};
